import type { ContractData } from '@/api/contract/types';
import { RouterV2Enum } from '@/router/router-names';
import platform from '@/shared/utils/platform';
import router from '@/router';
import NativeApi from '@/api/native';
import { contractsUrl, isH5App } from '@/constant';
import { isOpenLocationPermission } from '@/api/native/userPermit';
import {
  ubtSetupPageButton,
  ubtSetupModuleExpose,
} from '@/shared/utils/ubt/ubt-setup';
import store from '@/store';
import { Dialog } from '@hb/coffee';
import { noChannelResultMap } from '@/views_v2/pre-apply-choose/Configs';
import { credittingSpecialList } from '@/shared/contract-controller/config';
import { checkIfPdfLinkByExtension } from '@/shared/contract-controller';
import { LocationAlertStateEnum } from '@/enums/native';
import { jumpToMarketPage } from '@/shared/utils/jumpToRedirectPage';
import { ubtSendError } from '@/shared/utils/ubt';
import PreApplyChooseAPI from '@/api/preApplyChoose';
import updateGetDeviceInfo from './deviceInfo';

/**
 * 通用的GPS定位弹窗函数
 * @returns Promise<unknown>
 */
export const showGpsAlert = (): Promise<unknown> => {
  /** 是否是H5提现页 */
  const isH5ApplyPage = isH5App && router.currentRoute.path === RouterV2Enum.applyProduct;
  // 弹窗曝光埋点
  ubtSetupModuleExpose('finance_loan_locationPopUp');

  return Dialog.alert({
    message: isH5ApplyPage ? '该渠道提现受限，前往哈啰APP可正常进行提现' : '当前未获取到您的定位，若您已开启权限，可尝试关闭APP重新进入申请',
    confirmButtonText: isH5ApplyPage ? '去提现' : '知道了',
    closeable: false,
  }).then(() => {
    // 点击确认按钮埋点
    ubtSetupPageButton('finance_loan_locationPopUp');
    if (isH5ApplyPage) {
      jumpToMarketPage();
    }
  });
};

/**
 * 定位权限检查和处理选项
 */
interface LocationPermissionOptions {
  /** 是否进行二次权限检查（用户点击确定后再次检查），默认false */
  needDoubleCheck?: boolean;
  /** 是否检查城市信息（latitude），默认false */
  needCheckCityInfo?: boolean;
  /** 是否返回布尔值，默认false */
  returnBoolean?: boolean;
}

/**
 * 通用的定位权限检查和处理函数
 *
 * 功能说明：
 * - 检查设备定位权限是否开启
 * - 如果未开启，显示原生定位权限弹窗
 * - 支持二次权限检查和城市信息验证
 * - UBT埋点会自动从router获取pageId
 *
 * @param options 配置选项
 * @param options.needDoubleCheck 是否进行二次权限检查（用户点击确定后再次检查），默认false
 * @param options.needCheckCityInfo 是否检查城市信息（latitude），默认false
 * @param options.returnBoolean 是否返回布尔值，默认false
 * @returns 如果returnBoolean为true，返回Promise<boolean>，否则返回Promise<void>
 */
export const checkAndHandleLocationPermission = async (
  options: LocationPermissionOptions = {},
): Promise<boolean | void> => {
  const {
    needDoubleCheck = false,
    needCheckCityInfo = false,
    returnBoolean = false,
  } = options;

  try {
    /** 微信小程序无定位权限，直接返回true豁免判断 */
    if (platform.isWechatMini) {
      return returnBoolean ? Promise.resolve(true) : Promise.resolve();
    }
    /** 如果经纬度存在，直接返回true */
    if (store.state.base.cityInfo.latitude && store.state.base.cityInfo.longitude) {
      // 更新设备信息（仅在App环境）
      if (platform.isHelloApp) {
        await updateGetDeviceInfo();
      }
      return returnBoolean ? Promise.resolve(true) : Promise.resolve();
    }
    /** 先判定经纬度是否存在，不存在的情况下, 再去校验 定位权限是否开启 */
    const { isOpen } = await isOpenLocationPermission();
    if (!isOpen) {
      // 弹窗曝光埋点（自动从router获取pageId）
      ubtSetupModuleExpose('finance_loan_locationPopUp_hello');
      NativeApi.showLocationAlert(
        '借款申请需开启定位权限，否则将会申请失败',
      ).then(async (result) => {
        // UBT埋点：根据用户操作区分上报，传递 Content 字段（自动从router获取pageId）
        if (result?.state === LocationAlertStateEnum.CONFIRMED) {
          ubtSetupPageButton('finance_loan_locationPopUp_hello', {
            Content: '确定',
          });
        } else if (result?.state === LocationAlertStateEnum.CANCELED) {
          ubtSetupPageButton('finance_loan_locationPopUp_hello', {
            Content: '取消',
          });
        }

        // 如果返回的state是0（确认），才执行后续逻辑
        if (result?.state === LocationAlertStateEnum.CONFIRMED) {
          // 更新设备信息（仅在App环境）
          if (platform.isHelloApp) {
            await updateGetDeviceInfo();
          }

          // 如果需要二次检查权限, 流程拦截
          if (needDoubleCheck) {
            /** 二次检查权限的时候，经纬度不存在，再去校验 定位权限是否开启 */
            if (!store.state.base.cityInfo.latitude && !store.state.base.cityInfo.longitude) {
              // eslint-disable-next-line no-console
              const checkResult = await isOpenLocationPermission();
              if (!checkResult.isOpen) {
                showGpsAlert();
              }
            }
          }
        }
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('showLocationAlert error', error);
      });

      return returnBoolean ? Promise.resolve(false) : Promise.resolve();
    }

    /** 权限已开启时, 更新设备信息 */
    if (platform.isHelloApp) {
      await updateGetDeviceInfo();
    }

    // 如果需要检查城市信息
    if (needCheckCityInfo) {
      if (!store.state.base.cityInfo.latitude && !store.state.base.cityInfo.longitude) {
        showGpsAlert();
        return returnBoolean ? Promise.resolve(false) : Promise.resolve();
      }
    }

    return returnBoolean ? Promise.resolve(true) : Promise.resolve();
  } catch (error) {
    return returnBoolean ? Promise.resolve(false) : Promise.resolve();
  }
};

/**
 * 检查定位权限（完整版本，包含二次检查和城市信息验证）
 *
 * 功能说明：
 * - 检查定位权限是否开启
 * - 进行二次权限检查（用户点击确定后再次检查）
 * - 验证城市信息是否存在
 * - UBT埋点会自动从router获取pageId
 *
 * @returns Promise<boolean> 返回定位权限是否开启
 */
export const checkIsOpenGPS = async (): Promise<boolean> => checkAndHandleLocationPermission({
  needDoubleCheck: true,
  needCheckCityInfo: true,
  returnBoolean: true,
}) as Promise<boolean>;

// 根据 forceRead 对协议进行排序
export const sortAgreements = (agreements: ContractData[]): ContractData[] => {
  const forceReadAgreements = agreements.filter((item: ContractData) => {
    if (item.forceRead && item.forceRead === '1') {
      return true;
    }
    return false;
  });
  const commonAgreements = agreements.filter((item: ContractData) => {
    if (item.forceRead && item.forceRead === '1') {
      return false;
    }
    return true;
  });
  return [...forceReadAgreements, ...commonAgreements];
};

/** 格式化协议名称（截断超过9字并添加省略号） */
export const formatContractName = (name: string): string => {
  // 1. 去除首尾的<、》、《、>符号
  const processedName = name
    .trim()
    .replace(/^[\u003c|《]?(.+?)[\u003e|》]?$/, '$1');

  // 2. 截断处理（超过9字则显示前9字+...）
  const truncatedName = processedName.length > 9
    ? `${processedName.slice(0, 9)}...`
    : processedName;

  // 3. 添加书名号
  return `《${truncatedName}》`;
};

/**
 * 是否展示pdf预览，历史渠道配置、pdf链接动态识别
 * @param commodityId 商品id
 * @param url 链接
 * @returns 是否展示pdf预览
 */
export const shouldPreviewPdfForCredit = (
  commodityId: number | string,
  url: string,
): boolean => credittingSpecialList.includes(String(commodityId)) || checkIfPdfLinkByExtension(url);

/** 获取协议链接
 * pdf.js 底层目前最新的逻辑是不会解码的。后续遇到有渠道需要编码或者解码的时候，需要把这块逻辑前置加上，不放到 pdf.js 里面
 * 渠道返回的协议链接是pdf，在安卓手机会存在直接跳转下载的问题
 */
export const getContractIframeUrl = (commodityId: number, url: string): string => {
  if (shouldPreviewPdfForCredit(commodityId, url)) {
    return `${contractsUrl || ''}?pdf_url=${url}`;
  }
  return url;
};

/**
 * 获取中收变现外跳链接
 * @returns 返回中收变现外跳链接，未准入或接口失败时返回 undefined
 */
export const getMiddleIncomeLink = async (): Promise<string | undefined> => {
  // 只在哈啰app环境下检查车抵贷准入
  if (!platform.isEasyBike) {
    return undefined;
  }

  try {
    const middleIncomeLinkInfo = await PreApplyChooseAPI.getMiddleIncomeLinkInfo();

    // 接口返回url表示准入成功
    if (middleIncomeLinkInfo?.url) {
      return middleIncomeLinkInfo.url;
    }

    return undefined;
  } catch (error) {
    // 接口失败或未准入，继续执行原有逻辑
    ubtSendError(`getMiddleIncomeLink error: ${JSON.stringify(error)}`);
    return undefined;
  }
};

/** 无分发渠道时跳转逻辑
 * 根据当前浏览器环境选择对应的跳转页面
 * - 微信小程序：跳转到授信结果页
 * - 支付宝小程序/钉钉：跳转下载页
 * - 其他入口（主要是哈啰APP）：跳转贷超列表页
 * @param openUrl 可选的中收变现外跳链接，仅在跳转贷超列表页时生效，用于携带到目标页面
  */
export const chooseNoChannelRouter = (openUrl?: string):void => {
  /** 不是授信页面路由则不执行跳转逻辑 */
  if (
    ![
      RouterV2Enum.preApplyChoose,
      RouterV2Enum.dcPreApplyChoose,
    ].includes(router.currentRoute.path as RouterV2Enum)
  ) {
    return;
  }
  let jumpUrl: string | undefined;
  const { platform: currentPlatformType } = platform.currentPlatformType || {};

  /** 微信小程序，不支持贷超，直接跳转到结果页（不使用openUrl） */
  if (platform.isWechatMini) {
    router.replace(RouterV2Enum.preApplyResult);
    return;
  }

  /** 支付宝小程序/钉钉，跳转到下载页（不使用openUrl） */
  if (
    noChannelResultMap.has(Number(currentPlatformType))
    && (platform.isAlipayMini || platform.isDingTalk)) {
    jumpUrl = noChannelResultMap.get(Number(currentPlatformType)) as string;
    router.replace(jumpUrl);
    return;
  }

  /** 其他场景（主要是哈啰APP），跳转到贷超列表页 */
  jumpUrl = `${noChannelResultMap.get(0) as string}`;

  /** 如果有车抵贷链接，携带openUrl参数到贷超列表页，用于车抵贷导流 */
  if (openUrl) {
    jumpUrl = `${jumpUrl}&openUrl=${encodeURIComponent(openUrl)}`;
  }

  router.replace(jumpUrl);
};

/**
 * 通用的定位权限检查和处理函数
 *
 * 功能说明：
 * - 检查设备定位权限是否开启
 * - 如果未开启，显示原生定位权限弹窗
 * - 支持二次权限检查和城市信息验证
 * - UBT埋点会自动从router获取pageId
 *
 * @param options 配置选项
 * @param options.needDoubleCheck 是否进行二次权限检查（用户点击确定后再次检查），默认false
 * @param options.needCheckCityInfo 是否检查城市信息（latitude），默认false
 * @param options.returnBoolean 是否返回布尔值，默认false
 * @returns 如果returnBoolean为true，返回Promise<boolean>，否则返回Promise<void>
 */
export const newCheckAndHandleLocationPermission = async (
  options: LocationPermissionOptions = {},
): Promise<boolean | void> => {
  const {
    needDoubleCheck = false,
    needCheckCityInfo = false,
    returnBoolean = false,
  } = options;

  try {
    /** 微信小程序无定位权限，直接返回true豁免判断 */
    if (platform.isWechatMini) {
      return returnBoolean ? Promise.resolve(true) : Promise.resolve();
    }
    /** 如果经纬度存在，直接返回true */
    if (store.state.base.cityInfo.latitude && store.state.base.cityInfo.longitude) {
      // 更新设备信息（仅在App环境）
      if (platform.isHelloApp) {
        await updateGetDeviceInfo();
      }
      return returnBoolean ? Promise.resolve(true) : Promise.resolve();
    }
    /** 先判定经纬度是否存在，不存在的情况下, 再去校验 定位权限是否开启 */
    const { isOpen } = await isOpenLocationPermission();
    if (!isOpen) {
      // 弹窗曝光埋点（自动从router获取pageId）
      ubtSetupModuleExpose('finance_loan_locationPopUp_hello');

      try {
        const result = await NativeApi.showLocationAlert(
          '借款申请需开启定位权限，否则将会申请失败',
        );

        // UBT埋点：根据用户操作区分上报，传递 Content 字段（自动从router获取pageId）
        if (result?.state === LocationAlertStateEnum.CONFIRMED) {
          ubtSetupPageButton('finance_loan_locationPopUp_hello', {
            Content: '确定',
          });
        } else if (result?.state === LocationAlertStateEnum.CANCELED) {
          ubtSetupPageButton('finance_loan_locationPopUp_hello', {
            Content: '取消',
          });
        }

        // 如果返回的state是0（确认），才执行后续逻辑
        if (result?.state === LocationAlertStateEnum.CONFIRMED) {
          // 更新设备信息（仅在App环境）
          if (platform.isHelloApp) {
            await updateGetDeviceInfo();
          }

          // 如果需要二次检查权限, 流程拦截
          if (needDoubleCheck) {
            /** 二次检查权限的时候，经纬度不存在，再去校验 定位权限是否开启 */
            if (!store.state.base.cityInfo.latitude && !store.state.base.cityInfo.longitude) {
              const checkResult = await isOpenLocationPermission();
              if (!checkResult.isOpen) {
                showGpsAlert();
              }
            } else {
              return returnBoolean ? Promise.resolve(true) : Promise.resolve();
            }
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('showLocationAlert error', error);
      }

      return returnBoolean ? Promise.resolve(false) : Promise.resolve();
    }

    /** 权限已开启时, 更新设备信息 */
    if (platform.isHelloApp) {
      await updateGetDeviceInfo();
    }

    // 如果需要检查城市信息
    if (needCheckCityInfo) {
      if (!store.state.base.cityInfo.latitude && !store.state.base.cityInfo.longitude) {
        showGpsAlert();
        return returnBoolean ? Promise.resolve(false) : Promise.resolve();
      }
    }

    return returnBoolean ? Promise.resolve(true) : Promise.resolve();
  } catch (error) {
    return returnBoolean ? Promise.resolve(false) : Promise.resolve();
  }
};
